import React from "react";
import { PageHeaderLink } from "component/shared/page-header";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { Alert } from "@mui/material";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { Tag } from "component/shared/tag";
import { FullWidthDropdown } from "component/shared/full-width-dropdown";
import { LimitSoldResultsByMonthsType } from "type/limit-sold-results-by-months";
import { ActiveResultsSortType } from "type/active-results-sort";
import { EurekaResultsSortType } from "type/eureka-results-sort";
import { SearchAgentCaptureFieldType } from "type/search-agent-capture-field";
import { PageType } from "type/page-type";
import { User } from "model/user";
import { Market } from "model/market";
import { RootState } from "redux/store";
import { saveUser } from "redux/slice/authentication";
import { fetchPages } from "redux/slice/pages";
import { getPages } from "redux/selector";
import { getMarkets, getPromotedMarkets, getNaturalLanguageSearchSuggestions, getAreaSelectorPlaceholders } from "redux/selector";
import {
	fetchNaturalLanguageSearchSuggestions,
	createNaturalLanguageSearchSuggestion,
	updateNaturalLanguageSearchSuggestion,
	deleteNaturalLanguageSearchSuggestion,
} from "redux/slice/natural-language-search-suggestions";
import {
	fetchAreaSelectorPlaceholders,
	createAreaSelectorPlaceholder,
	updateAreaSelectorPlaceholder,
	deleteAreaSelectorPlaceholder,
} from "redux/slice/area-selector-placeholders";
import { updateMarket } from "redux/slice/markets";
import { PageProps } from "shared/page-props";
import * as Icons from "react-feather";
import * as Router from "react-router-dom";
import * as paths from "routes/paths";
import * as env from "shared/env";
// api/api, not api/live: importing live directly bypasses mock mode.
import * as api from "api/api";
import { ConfirmationModal } from "./confirmation-modal";
import { SwitchToStandardDialog } from "./switch-to-standard-dialog";
import { PriceRangeOptionsModal, formatPrice } from "./price-range-options-modal";
import { MarketsModal } from "./markets-modal";
import { TextListModal } from "./text-list-modal";
import { SearchSettingsTabs, SearchSettingsTab } from "./search-settings-tabs";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { UnsavedChangesPrompt } from "component/shared/unsaved-changes-prompt";
import { styles } from "./style";

const mapStateToProps = (state: RootState) => ({
	authLoading: state.authentication.loading,
	authError: state.authentication.error,
	allMarkets: getMarkets(state),
	promotedMarkets: getPromotedMarkets(state),
	suggestions: getNaturalLanguageSearchSuggestions(state),
	placeholders: getAreaSelectorPlaceholders(state),
	pages: getPages(state),
});

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators({
		saveUser,
		fetchPages,
		fetchNaturalLanguageSearchSuggestions,
		createNaturalLanguageSearchSuggestion,
		updateNaturalLanguageSearchSuggestion,
		deleteNaturalLanguageSearchSuggestion,
		fetchAreaSelectorPlaceholders,
		createAreaSelectorPlaceholder,
		updateAreaSelectorPlaceholder,
		deleteAreaSelectorPlaceholder,
		updateMarket,
	}, dispatch);

interface Props
	extends PageProps,
	Router.RouteComponentProps,
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>,
	WithStyles<typeof styles> { }

interface State {
	tab: SearchSettingsTab;
	searchType: "location" | "smart" | "both";
	showSavedSearchesInAreaSelector: boolean;
	showMarketsInAreaSelector: boolean;
	showNaturalLanguageSearchSuggestions?: boolean;
	displayMapGridResults: boolean;
	displayMapListResults: boolean;
	hideSoldSearch: boolean; // INVERTED: true = hide, false = show
	showClientListingsFirst: boolean;
	listingDetailModal: boolean;
	naturalLanguageSearch: boolean;
	naturalLanguageSearchOnly: boolean;
	showSavedSearches: boolean;
	showMarkets: boolean;
	showSearchSuggestions: boolean;
	markets: string[];
	searchSuggestions: string[];
	locationPlaceholder: string;
	useCustomPlaceholder: boolean;
	// MAX-3820/MAX-2701: Standard Search "Use Custom Placeholder Text" toggle (distinct from the
	// Smart Search useCustomPlaceholder above).
	useCustomLocationPlaceholder: boolean;
	searchAgentEnabled: boolean;
	searchAgentCaptureFields: string[];
	smartSuggestions: string[];
	defaultResultsView: "map" | "list";
	showSoldListings: boolean;
	prioritizeOwnListings: boolean;
	limitSoldResultsByMonths: LimitSoldResultsByMonthsType;
	showCustomPriceRangeOptions: boolean;
	minPriceOptions: number[];
	maxPriceOptions: number[];
	priceRangeModalOpen: boolean;
	eurekaResultsSort: EurekaResultsSortType;
	activeResultsSort: ActiveResultsSortType;
	showConfirmationModal: boolean;
	showSwitchToStandardModal: boolean;
	showMarketsModal: boolean;
	showSuggestionsModal: boolean;
	showPlaceholdersModal: boolean;
	pendingSearchType: "smart" | "both" | null;
	saving: boolean;
	snackbarOpen: boolean;
	snackbarMessage: string;
	// MAX-3820/MAX-2701: true after a submit with the Standard Search placeholder blank while its toggle is on.
	submittedLocationPlaceholderError: boolean;
}

class Component extends React.Component<Props, State> {
	private static readonly MAX_MARKETS = 5;
	private static readonly MAX_NLS_TERMS = 5;
	private static readonly MAX_NLS_PLACEHOLDERS = 5;
	private loadedPriceRangeOptions = { minPrices: [] as number[], maxPrices: [] as number[] };

	private initMarkets(userMarkets: number[] | undefined): string[] {
		const length = Component.MAX_MARKETS;
		return Array.from({ length }, (_, i) => userMarkets?.[i]?.toString() ?? "");
	}

	private initSearchSuggestions(userTerms: string[] | undefined): string[] {
		const length = Component.MAX_NLS_TERMS;
		return Array.from({ length }, (_, i) => userTerms?.[i] ?? "");
	}

	private initSmartSuggestions(userPlaceholders: string[] | undefined): string[] {
		const length = Component.MAX_NLS_PLACEHOLDERS;
		return Array.from({ length }, (_, i) => userPlaceholders?.[i] ?? "");
	}

	/**
	 * Whether the Search Agent settings may be shown and saved for this client.
	 *
	 * Requires the Search Agent billing entitlement, checked strictly so that a client without it
	 * never sees the control. The API ignores these fields on a PUT from an un-entitled client, so
	 * the check keeps the UI and the API in agreement.
	 */
	private get searchAgentPermitted(): boolean {
		return this.props.user.permissions.aiSearchAgent === true;
	}

	// Normalizes to the canonical SearchAgentCaptureFieldType order so dirty comparison and save payloads
	// don't depend on the order the boxes were checked in, and unknown ids are dropped.
	private initSearchAgentCaptureFields(userCaptureFields: string[] | undefined): string[] {
		return SearchAgentCaptureFieldType.values()
			.filter((value) => userCaptureFields?.includes(value.id))
			.map((value) => value.id);
	}

	public constructor(props: Props) {
		super(props);
		const { user } = props;

		const searchType = this.savedSearchType();

		// Match settings-listing-results logic: check the field for current display mode
		const isGridMode = user.listingResultsDisplay?.grid ?? false;
		const mapVisibleForCurrentMode = isGridMode
			? user.displayMapGridResults
			: user.displayMapListResults;
		const defaultResultsView: "map" | "list" = mapVisibleForCurrentMode ? "map" : "list";

		const showSavedSearches = user.showSavedSearchesInAreaSelector !== false;
		const showMarkets = user.showMarketsInAreaSelector !== false;
		const showSearchSuggestions = user.showNaturalLanguageSearchSuggestions !== false;
		const markets = this.initMarkets(props.promotedMarkets?.map(m => m.id));
		const searchSuggestions = this.initSearchSuggestions(props.suggestions?.map(s => s.nls));
		const useCustomPlaceholder = user.showCustomPlaceholdersInAreaSelector !== false;
		const smartSuggestions = this.initSmartSuggestions(props.placeholders?.map(p => p.text));
		// MAX-3820/MAX-2701: Standard Search placeholder. Custom is "on" when a non-empty value is set.
		const useCustomLocationPlaceholder = !!(user.searchLocationFieldText && user.searchLocationFieldText.trim() !== "");
		const locationPlaceholder = user.searchLocationFieldText || "";

		this.state = {
			...user,
			tab: "experience",
			searchType,
			defaultResultsView,
			showSoldListings: !user.hideSoldSearch, // INVERTED
			prioritizeOwnListings: user.showClientListingsFirst,
			limitSoldResultsByMonths: user.limitSoldResultsByMonths || LimitSoldResultsByMonthsType.getByIdOrDefault(0),
			eurekaResultsSort: EurekaResultsSortType.getByIdOrDefault(user.eurekaResultsSort?.id),
			activeResultsSort: ActiveResultsSortType.getByIdOrDefault(user.activeResultsSort?.id),
			showSavedSearches,
			showMarkets,
			showSearchSuggestions,
			markets,
			searchSuggestions,
			smartSuggestions,
			locationPlaceholder,
			useCustomPlaceholder,
			useCustomLocationPlaceholder,
			searchAgentEnabled: user.searchAgentEnabled ?? false,
			searchAgentCaptureFields: this.initSearchAgentCaptureFields(user.searchAgentCaptureFields),
			showCustomPriceRangeOptions: user.showCustomPriceRangeOptions ?? false,
			minPriceOptions: [],
			maxPriceOptions: [],
			priceRangeModalOpen: false,
			showConfirmationModal: false,
			showSwitchToStandardModal: false,
			showMarketsModal: false,
			showSuggestionsModal: false,
			showPlaceholdersModal: false,
			pendingSearchType: null,
			saving: false,
			snackbarOpen: false,
			snackbarMessage: "",
			submittedLocationPlaceholderError: false,
		};
	}

	public componentDidMount() {
		const { user } = this.props;
		this.props.fetchPages();
		if (user.permissions.aiNaturalLanguageSearch) {
			this.props.fetchNaturalLanguageSearchSuggestions();
		}
		this.props.fetchAreaSelectorPlaceholders();
		api.getPriceRangeOptions().then((result) => {
			this.loadedPriceRangeOptions = { minPrices: result.minPrices, maxPrices: result.maxPrices };
			this.setState({ minPriceOptions: result.minPrices, maxPriceOptions: result.maxPrices });
		});
	}

	private isDirty(): boolean {
		const { user, promotedMarkets, suggestions, placeholders } = this.props;
		const {
			searchType, showSoldListings, prioritizeOwnListings, limitSoldResultsByMonths,
			eurekaResultsSort, activeResultsSort, showSavedSearches, showMarkets,
			showSearchSuggestions, markets, searchSuggestions, useCustomPlaceholder, smartSuggestions,
			useCustomLocationPlaceholder, locationPlaceholder, searchAgentEnabled, searchAgentCaptureFields,
			showCustomPriceRangeOptions, minPriceOptions, maxPriceOptions, listingDetailModal,
		} = this.state;

		// MAX-3820/MAX-2701: Standard Search placeholder dirty comparison.
		const userUseCustomLocationPlaceholder = !!(user.searchLocationFieldText && user.searchLocationFieldText.trim() !== "");
		const userLocationPlaceholder = user.searchLocationFieldText || "";

		const userSearchType = this.savedSearchType();

		const userMarkets = this.initMarkets(promotedMarkets?.map(m => m.id));
		const userSuggestions = this.initSearchSuggestions(suggestions?.map(s => s.nls));
		const userSmartSuggestions = this.initSmartSuggestions(placeholders?.map(p => p.text));

		return (
			searchType !== userSearchType ||
			showSoldListings !== !user.hideSoldSearch ||
			prioritizeOwnListings !== user.showClientListingsFirst ||
			limitSoldResultsByMonths?.id !== (user.limitSoldResultsByMonths?.id ?? LimitSoldResultsByMonthsType.getByIdOrDefault(0).id) ||
			eurekaResultsSort?.id !== EurekaResultsSortType.getByIdOrDefault(user.eurekaResultsSort?.id).id ||
			activeResultsSort?.id !== ActiveResultsSortType.getByIdOrDefault(user.activeResultsSort?.id).id ||
			showSavedSearches !== (user.showSavedSearchesInAreaSelector !== false) ||
			showMarkets !== (user.showMarketsInAreaSelector !== false) ||
			showSearchSuggestions !== (user.showNaturalLanguageSearchSuggestions !== false) ||
			useCustomPlaceholder !== (user.showCustomPlaceholdersInAreaSelector !== false) ||
			useCustomLocationPlaceholder !== userUseCustomLocationPlaceholder ||
			locationPlaceholder !== userLocationPlaceholder ||
			// Compared as the Search Agent would be saved, since Standard always saves it off.
			(searchAgentEnabled && searchType !== "location") !== ((user.searchAgentEnabled ?? false) && userSearchType !== "location") ||
			JSON.stringify(searchAgentCaptureFields) !== JSON.stringify(this.initSearchAgentCaptureFields(user.searchAgentCaptureFields)) ||
			JSON.stringify(markets) !== JSON.stringify(userMarkets) ||
			JSON.stringify(searchSuggestions) !== JSON.stringify(userSuggestions) ||
			JSON.stringify(smartSuggestions) !== JSON.stringify(userSmartSuggestions) ||
			showCustomPriceRangeOptions !== (user.showCustomPriceRangeOptions ?? false) ||
			JSON.stringify(minPriceOptions) !== JSON.stringify(this.loadedPriceRangeOptions.minPrices) ||
			JSON.stringify(maxPriceOptions) !== JSON.stringify(this.loadedPriceRangeOptions.maxPrices) ||
			listingDetailModal !== user.listingDetailModal
		);
	}

	public componentDidUpdate(prevProps: Props) {
		const { authLoading, authError, promotedMarkets, suggestions, placeholders } = this.props;
		if (prevProps.authLoading && !authLoading && !authError && this.state.saving) {
			this.setState({ saving: false });
		}
		if (prevProps.authLoading && !authLoading && authError && this.state.saving) {
			this.setState({
				saving: false,
				snackbarMessage: "Failed to save settings",
			});
		}
		if (prevProps.promotedMarkets !== promotedMarkets && !this.state.saving) {
			const markets = this.initMarkets(promotedMarkets?.map(m => m.id));
			this.setState({ markets });
		}
		if (prevProps.suggestions !== suggestions) {
			const searchSuggestions = this.initSearchSuggestions(suggestions?.map(s => s.nls));
			this.setState({ searchSuggestions });
		}
		if (prevProps.placeholders !== placeholders) {
			const smartSuggestions = this.initSmartSuggestions(placeholders?.map(p => p.text));
			this.setState({ smartSuggestions });
		}
	}

	private handleSearchTypeChange = (value: "location" | "smart" | "both") => {
		if (value === "smart" || value === "both") {
			const hasConfirmed = localStorage.getItem("smartSearchConfirmed") === "true";

			if (hasConfirmed) {
				this.setState({ searchType: value });
			} else {
				this.setState({
					pendingSearchType: value,
					showConfirmationModal: true,
				});
			}
		} else if (this.searchAgentPermitted && this.savedSearchType() !== "location") {
			// The radio stays on the previous option until the switch is confirmed.
			this.setState({ showSwitchToStandardModal: true });
		} else {
			this.setState({ searchType: value });
		}
	};

	// The Search Agent requires Smart Search, so leaving it turns the Search Agent off. Nothing is
	// saved until Save Changes, and the page-level Cancel restores both.
	private handleSwitchToStandardConfirm = () => {
		this.setState({
			searchType: "location",
			searchAgentEnabled: false,
			showSwitchToStandardModal: false,
		});
	};

	private handleSwitchToStandardCancel = () => {
		this.setState({ showSwitchToStandardModal: false });
	};

	// The last saved Search Functionality, which decides whether switching to Standard warns.
	private savedSearchType(): "location" | "smart" | "both" {
		const { user } = this.props;
		if (!user.permissions.aiNaturalLanguageSearch || user.naturalLanguageSearch === false) {
			return "location";
		}
		return user.naturalLanguageSearchOnly === true ? "smart" : "both";
	}

	private handleModalConfirm = () => {
		const { pendingSearchType } = this.state;
		if (pendingSearchType) {
			localStorage.setItem("smartSearchConfirmed", "true");
			this.setState({
				searchType: pendingSearchType,
				showConfirmationModal: false,
				pendingSearchType: null,
			});
		}
	};

	private handleModalCancel = () => {
		this.setState({
			showConfirmationModal: false,
			pendingSearchType: null,
		});
	};

	private handleSave = async () => {
		const { saveUser, user } = this.props;
		const {
			searchType,
			defaultResultsView,
			showSoldListings,
			prioritizeOwnListings,
			limitSoldResultsByMonths,
			eurekaResultsSort,
			activeResultsSort,
			showSavedSearches,
			showMarkets,
			showSearchSuggestions,
			markets,
			searchSuggestions,
			useCustomPlaceholder,
			useCustomLocationPlaceholder,
			locationPlaceholder,
			searchAgentEnabled,
			searchAgentCaptureFields,
			smartSuggestions,
			showCustomPriceRangeOptions,
			minPriceOptions,
			maxPriceOptions,
			listingDetailModal,
		} = this.state;

		// MAX-3820/MAX-2701: Standard Search placeholder is required when its custom toggle is on.
		const standardPlaceholderActive = searchType === "location" || searchType === "both";
		if (standardPlaceholderActive && useCustomLocationPlaceholder && locationPlaceholder.trim() === "") {
			this.setState({
				submittedLocationPlaceholderError: true,
				snackbarOpen: true,
				snackbarMessage: "Search placeholder text is required.",
			});
			return;
		}
		this.setState({ submittedLocationPlaceholderError: false });

		const naturalLanguageSearch = searchType === "location" ? false : true;
		const naturalLanguageSearchOnly = searchType === "smart" ? true : false;

		// Match settings-listing-results logic: only update the field for the current display mode
		const isGridMode = user.listingResultsDisplay?.grid ?? false;
		const displayMapGridResults = isGridMode ? (defaultResultsView === "map") : user.displayMapGridResults;
		const displayMapListResults = !isGridMode ? (defaultResultsView === "map") : user.displayMapListResults;
		const showSavedSearchesInAreaSelector = showSavedSearches;
		const showMarketsInAreaSelector = showMarkets;

		// Handle Markets: Update Market entities' promotedInAreaSelector property
		if (showMarkets) {
			// Get currently selected market IDs from UI state
			const selectedMarketIds = markets
				.filter(id => id && id !== "")
				.map(id => parseInt(id, 10));

			// Get all available markets from props
			const allMarkets = this.props.allMarkets || [];

			// Get currently promoted market IDs (from promotedMarkets prop, not from allMarkets property)
			// because allMarkets may not have promotedInAreaSelector property populated
			const currentlyPromotedIds = this.props.promotedMarkets?.map(m => m.id) || [];

			// Find markets that need to be toggled
			allMarkets.forEach(market => {
				const isSelected = selectedMarketIds.includes(market.id);
				const wasSelected = currentlyPromotedIds.includes(market.id);

				if (isSelected !== wasSelected) {
					// Toggle the market's promotedInAreaSelector property
					this.props.updateMarket({
						market: {
							...market,
							promotedInAreaSelector: isSelected,
						} as Market
					});
				}
			});
		} else {
			// If showMarkets is false, unpromote all markets
			const allMarkets = this.props.allMarkets || [];
			allMarkets.forEach(market => {
				if (market.promotedInAreaSelector) {
					this.props.updateMarket({
						market: {
							...market,
							promotedInAreaSelector: false,
						} as Market
					});
				}
			});
		}

		// Handle NLS suggestions: Process sequentially to preserve order
		const existingSuggestions = this.props.suggestions || [];

		// Delete suggestions that are no longer in the list (can be parallel)
		existingSuggestions.forEach(suggestion => {
			const stillExists = searchSuggestions.some(text => text.trim() === suggestion.nls);
			if (!stillExists) {
				this.props.deleteNaturalLanguageSearchSuggestion({ id: suggestion.id });
			}
		});

		// Create new suggestions SEQUENTIALLY to preserve order
		for (let i = 0; i < searchSuggestions.length; i++) {
			const text = searchSuggestions[i].trim();
			if (text !== "") {
				const existing = existingSuggestions.find(s => s.nls === text);
				if (!existing) {
					await this.props.createNaturalLanguageSearchSuggestion({
						suggestion: { nls: text, promoted: true }
					});
				}
			}
		}

		// Handle Placeholders: Process sequentially to preserve order
		const existingPlaceholders = this.props.placeholders || [];

		// Delete placeholders that are no longer in the list (can be parallel)
		existingPlaceholders.forEach(placeholder => {
			const stillExists = smartSuggestions.some(text => text.trim() === placeholder.text);
			if (!stillExists) {
				this.props.deleteAreaSelectorPlaceholder({ id: placeholder.id });
			}
		});

		// Create new placeholders SEQUENTIALLY to preserve order
		for (let i = 0; i < smartSuggestions.length; i++) {
			const text = smartSuggestions[i].trim();
			if (text !== "") {
				const existing = existingPlaceholders.find(p => p.text === text);
				if (!existing) {
					await this.props.createAreaSelectorPlaceholder({
						placeholder: { text: text }
					});
				}
			}
		}

		const userPartial: Partial<User> = {
			naturalLanguageSearch,
			naturalLanguageSearchOnly,
			showSavedSearchesInAreaSelector,
			showMarketsInAreaSelector,
			showNaturalLanguageSearchSuggestions: showSearchSuggestions,
			showCustomPlaceholdersInAreaSelector: useCustomPlaceholder,
			// MAX-3820/MAX-2701: Standard Search placeholder text. When the toggle is off we clear it so
			// the site falls back to the default placeholder.
			searchLocationFieldText: useCustomLocationPlaceholder ? locationPlaceholder : "",
			// Omitted for un-entitled clients so we never write settings they could not change.
			...(this.searchAgentPermitted ? {
				// The Search Agent requires Smart Search, so Standard always saves it off.
				searchAgentEnabled: searchAgentEnabled && searchType !== "location",
				// Keep the capture-field selections when the agent is switched off so turning it back on restores them.
				searchAgentCaptureFields,
			} : {}),
			displayMapGridResults,
			displayMapListResults,
			hideSoldSearch: !showSoldListings,
			showClientListingsFirst: prioritizeOwnListings,
			limitSoldResultsByMonths,
			showCustomPriceRangeOptions,
			eurekaResultsSort,
			activeResultsSort,
			listingDetailModal,
		};

		// Merge with current user to preserve other properties (like colorScheme)
		saveUser({ user: { ...user, ...userPartial } });

		// Fire price range save in parallel (only when feature is enabled)
		const priceRangeSave = showCustomPriceRangeOptions
			? api.savePriceRangeOptions({ minPrices: minPriceOptions, maxPrices: maxPriceOptions })
			: Promise.resolve(null);

		if (searchType === "location") {
			localStorage.removeItem("smartSearchConfirmed");
		}

		this.setState({
			saving: true,
			snackbarOpen: true,
			snackbarMessage: "Settings saved successfully",
		});

		try {
			const result = await priceRangeSave;
			if (result) {
				this.loadedPriceRangeOptions = { minPrices: result.minPrices, maxPrices: result.maxPrices };
				this.setState({ minPriceOptions: result.minPrices, maxPriceOptions: result.maxPrices });
			}
		} catch(error) {
			this.setState({ saving: false, snackbarOpen: true, snackbarMessage: "Failed to save settings" });
		}
	};

	private handleCancel = () => {
		const { user, promotedMarkets, suggestions, placeholders } = this.props;

		const searchType = this.savedSearchType();

		// Match settings-listing-results logic: check the field for current display mode
		const isGridMode = user.listingResultsDisplay?.grid ?? false;
		const mapVisibleForCurrentMode = isGridMode
			? user.displayMapGridResults
			: user.displayMapListResults;
		const defaultResultsView: "map" | "list" = mapVisibleForCurrentMode ? "map" : "list";

		const showSavedSearches = user.showSavedSearchesInAreaSelector !== false;
		const showMarkets = user.showMarketsInAreaSelector !== false;
		const showSearchSuggestions = user.showNaturalLanguageSearchSuggestions !== false;
		const markets = this.initMarkets(promotedMarkets?.map(m => m.id));
		const searchSuggestions = this.initSearchSuggestions(suggestions?.map(s => s.nls));
		const useCustomPlaceholder = user.showCustomPlaceholdersInAreaSelector !== false;
		const smartSuggestions = this.initSmartSuggestions(placeholders?.map(p => p.text));

		this.setState({
			searchType,
			defaultResultsView,
			showSoldListings: !user.hideSoldSearch,
			prioritizeOwnListings: user.showClientListingsFirst,
			eurekaResultsSort: EurekaResultsSortType.getByIdOrDefault(user.eurekaResultsSort?.id),
			activeResultsSort: ActiveResultsSortType.getByIdOrDefault(user.activeResultsSort?.id),
			showSavedSearches,
			showMarkets,
			showSearchSuggestions,
			markets,
			searchSuggestions,
			useCustomPlaceholder,
			smartSuggestions,
			searchAgentEnabled: user.searchAgentEnabled ?? false,
			searchAgentCaptureFields: this.initSearchAgentCaptureFields(user.searchAgentCaptureFields),
			showCustomPriceRangeOptions: user.showCustomPriceRangeOptions ?? false,
			minPriceOptions: this.loadedPriceRangeOptions.minPrices,
			maxPriceOptions: this.loadedPriceRangeOptions.maxPrices,
			priceRangeModalOpen: false,
			snackbarOpen: false,
			listingDetailModal: user.listingDetailModal,
		});
	};

	// Event handlers for Search Results settings
	private handleDefaultResultsViewChange = (value: "map" | "list") => {
		this.setState({ defaultResultsView: value });
	};

	private handleShowSoldListingsChange = (value: boolean) => {
		this.setState({ showSoldListings: value });
	};

	private handlePrioritizeOwnListingsChange = (value: boolean) => {
		this.setState({ prioritizeOwnListings: value });
	};

	private handleSearchAgentCaptureFieldChange = (id: string, checked: boolean) => {
		this.setState(({ searchAgentCaptureFields }) => ({
			searchAgentCaptureFields: this.initSearchAgentCaptureFields(
				checked
					? [...searchAgentCaptureFields, id]
					: searchAgentCaptureFields.filter((value) => value !== id)
			),
		}));
	};

	private handleSearchAgentCaptureFieldsSelectAll = (checked: boolean) => {
		this.setState({
			searchAgentCaptureFields: checked ? SearchAgentCaptureFieldType.values().map((value) => value.id) : [],
		});
	};

	// The "Search Experience" tab — how visitors search.
	private renderSearchExperienceTab() {
		const { user, classes = {} } = this.props;
		const {
			searchType,
			showSavedSearches,
			showMarkets,
			showSearchSuggestions,
			markets,
			searchSuggestions,
			locationPlaceholder,
			useCustomPlaceholder,
			useCustomLocationPlaceholder,
			submittedLocationPlaceholderError,
			smartSuggestions,
			searchAgentEnabled,
			searchAgentCaptureFields,
		} = this.state;
		const isSmartSearchActive = searchType === "smart" || searchType === "both";
		// The Search Agent requires Smart Search: under Standard the toggle reads off and is disabled,
		// and the saved value follows (handleSave).
		const searchAgentOn = searchAgentEnabled && isSmartSearchActive;
		const allCaptureFieldsSelected = searchAgentCaptureFields.length === SearchAgentCaptureFieldType.values().length;
		const someCaptureFieldsSelected = searchAgentCaptureFields.length > 0;

		return (
			<>
				{user.permissions.aiNaturalLanguageSearch && (
					<Mui.Box mb={2}>
						<FullPageCard>
							<Mui.Box display="flex" alignItems="center" mb={2}>
								<Mui.Typography variant="h6" className={classes.sectionHeading} style={{ marginBottom: 0 }}>Search Functionality</Mui.Typography>
								<Mui.Tooltip title="Choose the search experience shown to website visitors.">
									<Mui.IconButton size="small" style={{ marginLeft: 8, padding: 0 }}><Icons.Info size={16} /></Mui.IconButton>
								</Mui.Tooltip>
							</Mui.Box>
							<Mui.RadioGroup value={searchType} onChange={(e) => this.handleSearchTypeChange(e.target.value as "location" | "smart" | "both")}>
								<Mui.Box mb={2}>
									<Mui.FormControlLabel
										value="location"
										control={<Mui.Radio color="secondary" className={classes.controlLabelControl} />}
										className={classes.controlLabel}
										label={
											<Mui.Box>
												<Mui.Typography style={{ fontWeight: "bold" }}>Standard Search</Mui.Typography>
												<Mui.Typography variant="body2" className={classes.controlLabelDescription}>The traditional search experience — website visitors search by location and filter by price, beds, baths, and more.</Mui.Typography>
											</Mui.Box>
										}
									/>
								</Mui.Box>
								<Mui.Box mb={2}>
									<Mui.FormControlLabel
										value="smart"
										control={<Mui.Radio color="secondary" className={classes.controlLabelControl} />}
										className={classes.controlLabel}
										label={
											<Mui.Box>
												<Mui.Box display="flex" alignItems="center">
													<Mui.Typography style={{ fontWeight: "bold", marginRight: 8 }}>Smart Search</Mui.Typography>
													<Mui.Chip
													icon={<Mui.SvgIcon style={{ fontSize: 14 }}><path d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z" /></Mui.SvgIcon>}
													label="Smart Search"
													className={classes.nlsChip}
												/>
											</Mui.Box>
											<Mui.Typography variant="body2" className={classes.controlLabelDescription}>Website visitors can search naturally using phrases like "3-bedroom homes under 500k with pool."</Mui.Typography>
										</Mui.Box>
										}
									/>
								</Mui.Box>
								<Mui.Box mb={1}>
									<Mui.FormControlLabel
										value="both"
										control={<Mui.Radio color="secondary" className={classes.controlLabelControl} />}
										className={classes.controlLabel}
										label={
											<Mui.Box>
												<Mui.Typography style={{ fontWeight: "bold" }}>Both (Website Visitors Choose)</Mui.Typography>
												<Mui.Typography variant="body2" className={classes.controlLabelDescription}>Allow website visitors to choose between Standard Search or Smart Search when searching listings on your website.</Mui.Typography>
											</Mui.Box>
										}
									/>
								</Mui.Box>
							</Mui.RadioGroup>
						</FullPageCard>
					</Mui.Box>
				)}

				{this.searchAgentPermitted && (
					<Mui.Box mb={2}>
						<FullPageCard>
							<Mui.Box display="flex" alignItems="center" mb={2}>
								<Mui.Typography variant="h6" className={classes.sectionHeading} style={{ marginBottom: 0 }}>Search Agent</Mui.Typography>
								<Tag label="Beta" variant="orange" size="small" className={classes.betaTag} />
							</Mui.Box>
							<Mui.FormControlLabel
								className={classes.controlLabel}
								disabled={!isSmartSearchActive}
								control={
									<Mui.Tooltip
										title={isSmartSearchActive ? "" : "Requires Smart Search or Both to be enabled under Search Functionality."}
										describeChild
									>
										{/* A span, because a disabled switch fires no hover events for the tooltip. */}
										<span>
											<Mui.Switch
												checked={searchAgentOn}
												disabled={!isSmartSearchActive}
												onChange={(e) => this.setState({ searchAgentEnabled: e.target.checked })}
												color="secondary"
												className={classes.switchControl}
											/>
										</span>
									</Mui.Tooltip>
								}
								label={
									<Mui.Box>
										<Mui.Typography className={classes.settingTitle}>Use Search Agent</Mui.Typography>
										<Mui.Typography variant="body2" className={classes.controlLabelDescription}>Allow website visitors to search for homes using a conversational, chat-style assistant that learns their preferences and enriches their lead profile with insights.</Mui.Typography>
									</Mui.Box>
								}
							/>
							{searchAgentOn && (
								<Mui.Box className={classes.searchAgentDisclosure}>
									<Mui.Box className={classes.searchAgentCaptureFields}>
										<Mui.Typography className={classes.searchAgentCaptureIntro}>
											Select the information you'd like the Search Agent to prioritize collecting from leads during conversations:
										</Mui.Typography>
										<Mui.Box>
											<Mui.Box className={classes.selectAllRow}>
												<Mui.FormControlLabel
													className={`${classes.searchAgentCaptureOption} ${classes.selectAllOption}`}
													control={
														<Mui.Checkbox
															checked={allCaptureFieldsSelected}
															indeterminate={someCaptureFieldsSelected && !allCaptureFieldsSelected}
															onChange={(e) => this.handleSearchAgentCaptureFieldsSelectAll(e.target.checked)}
															color="secondary"
														/>
													}
													label="Select All"
												/>
											</Mui.Box>
											<Mui.Box className={classes.searchAgentCaptureList}>
												{SearchAgentCaptureFieldType.values().map((item) => (
													<Mui.FormControlLabel
														key={item.id}
														className={classes.searchAgentCaptureOption}
														control={
															<Mui.Checkbox
																checked={searchAgentCaptureFields.includes(item.id)}
																onChange={(e) => this.handleSearchAgentCaptureFieldChange(item.id, e.target.checked)}
																color="secondary"
															/>
														}
														label={item.label}
													/>
												))}
											</Mui.Box>
										</Mui.Box>
									</Mui.Box>
								</Mui.Box>
							)}
						</FullPageCard>
					</Mui.Box>
				)}

				<Mui.Box mb={2}>
					<FullPageCard>
						<Mui.Box display="flex" alignItems="center" mb={1}>
							<Mui.Typography variant="h6" className={classes.sectionHeading} style={{ marginBottom: 0 }}>Search Dropdown Behavior</Mui.Typography>
							<Mui.Tooltip title="Control what appears in the search dropdown when a website visitor clicks the search bar.">
								<Mui.IconButton size="small" style={{ marginLeft: 8, padding: 0 }}><Icons.Info size={16} /></Mui.IconButton>
							</Mui.Tooltip>
						</Mui.Box>
						<Mui.Typography variant="body2" className={classes.bodyText}>
							When a website visitor clicks into the search bar, display the following in the dropdown:
						</Mui.Typography>

						<Mui.Grid container direction="column" style={{ gap: 15 }}>
							<Mui.Grid item>
								<Mui.FormControlLabel
									className={classes.controlLabel}
									control={
										<Mui.Checkbox
											checked={showSavedSearches}
											onChange={(e) => this.setState({ showSavedSearches: e.target.checked })}
											color="secondary"
											className={classes.controlLabelControl}
										/>
									}
									label={
										<Mui.Box>
											<Mui.Typography style={{ fontWeight: "bold" }}>Saved Searches</Mui.Typography>
											<Mui.Typography variant="body2" className={classes.controlLabelDescription}>Allow logged-in leads to access all their saved searches, including agent-created ones, from the search dropdown.</Mui.Typography>
										</Mui.Box>
									}
								/>
							</Mui.Grid>

							<Mui.Grid item>
								<Mui.FormControlLabel
									className={classes.controlLabel}
									control={
										<Mui.Checkbox
											checked={showMarkets}
											onChange={(e) => this.setState({ showMarkets: e.target.checked })}
											color="secondary"
											className={classes.controlLabelControl}
										/>
									}
									label={
										<Mui.Box>
											<Mui.Typography style={{ fontWeight: "bold" }}>Markets</Mui.Typography>
											<Mui.Typography variant="body2" className={classes.controlLabelDescription}>Show your pre-configured markets in the search dropdown. Markets are managed on the Markets page.</Mui.Typography>
										</Mui.Box>
									}
								/>
								{showMarkets && (
									<Mui.Box className={classes.checkboxSubContent}>
										{this.props.allMarkets.length === 0 ? (
											<Mui.Typography variant="body2" color="textSecondary">
												You don't have any markets yet.{" "}
												<Router.Link to={paths.markets} className={classes.marketLink}>
													Click here to add some.
												</Router.Link>
											</Mui.Typography>
										) : (
											<>
												<Mui.Button size="small" color="primary" className={classes.editButton} onClick={() => this.setState({ showMarketsModal: true })}>
													Edit <Icons.Edit size={13} style={{ marginLeft: 4 }} />
												</Mui.Button>
												<Mui.Typography variant="body2" className={classes.readOnlyList} style={{ marginBottom: 0 }}>
													{markets.filter(id => id && id !== "").map(id => {
														const market = this.props.allMarkets.find(m => m.id.toString() === id);
														return market?.name;
													}).filter(Boolean).join(", ") || "None selected"}
												</Mui.Typography>
											</>
										)}
									</Mui.Box>
								)}
							</Mui.Grid>

							{user.permissions.aiNaturalLanguageSearch && isSmartSearchActive && (
								<Mui.Grid item>
									<Mui.FormControlLabel
										className={classes.controlLabel}
										control={
											<Mui.Checkbox
												checked={showSearchSuggestions}
												onChange={(e) => this.setState({ showSearchSuggestions: e.target.checked })}
												color="secondary"
												className={classes.controlLabelControl}
											/>
										}
										label={
											<Mui.Box>
												<Mui.Box display="flex" alignItems="center">
													<Mui.Typography style={{ fontWeight: "bold", marginRight: 8 }}>Search Suggestions</Mui.Typography>
													<Mui.Chip
														icon={<Mui.SvgIcon style={{ fontSize: 14 }}><path d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z" /></Mui.SvgIcon>}
														label="Smart Search"
														className={classes.nlsChip}
													/>
												</Mui.Box>
												<Mui.Typography variant="body2" className={classes.controlLabelDescription}>Pre-populate the search dropdown with suggested Smart Search terms that leads can select to start a search (Smart Search only).</Mui.Typography>
											</Mui.Box>
										}
									/>
									{showSearchSuggestions && (
										<Mui.Box className={classes.checkboxSubContent}>
											<Mui.Button size="small" color="primary" className={classes.editButton} onClick={() => this.setState({ showSuggestionsModal: true })}>
												Edit <Icons.Edit size={13} style={{ marginLeft: 4 }} />
											</Mui.Button>
											<Mui.Typography variant="body2" className={classes.readOnlyList}>
												{searchSuggestions.filter(s => s.trim() !== "").join(", ") || "None added"}
											</Mui.Typography>
										</Mui.Box>
									)}
								</Mui.Grid>
							)}
						</Mui.Grid>
					</FullPageCard>
				</Mui.Box>

				{/*
				MAX-3820 / MAX-2701: "Search Placeholder Text" section. Consolidates the Standard Search
				placeholder ("Default text", relocated from the ColdFusion Location Customization page,
				backed by UserResource.searchLocationFieldText -> client feature SearchLocationFieldText)
				and the existing Smart Search placeholder into ONE card, matching Figma:
				  - Standard-only mode: Standard sub-section, no sub-heading, no chip.
				  - Smart-only mode:    Smart sub-section, "Smart Search" chip on the header.
				  - Both mode:          "Standard Search" and "Smart Search" sub-headings, divider between.
				Standard sub-section is flag-gated on coldFusionReplatform (backend just shipped); the Smart
				sub-section keeps its existing aiNaturalLanguageSearch permission gate.
			*/}
				{(() => {
					const showStandard = env.coldFusionReplatform && (searchType === "location" || searchType === "both");
					const showSmart = user.permissions.aiNaturalLanguageSearch && isSmartSearchActive;
					const bothSubSections = showStandard && showSmart;
					if (!showStandard && !showSmart) {
						return null;
					}
					return (
						<Mui.Box mb={2}>
							<FullPageCard>
								<Mui.Box display="flex" alignItems="center" mb={2}>
									<Mui.Typography variant="h6" className={classes.sectionHeading} style={{ marginBottom: 0 }}>Search Placeholder Text</Mui.Typography>
									{showSmart && !bothSubSections && (
										<Mui.Chip
											icon={<Mui.SvgIcon style={{ fontSize: 14 }}><path d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z" /></Mui.SvgIcon>}
											label="Smart Search"
											className={classes.nlsChip}
											style={{ marginLeft: 8 }}
										/>
									)}
								</Mui.Box>

								{showStandard && (
									<Mui.Box mb={bothSubSections ? 3 : 0}>
										{bothSubSections && (
											<Mui.Typography variant="subtitle2" className={classes.sectionSubheading} style={{ marginBottom: 8 }}>Standard Search</Mui.Typography>
										)}
										<Mui.FormControlLabel
											className={classes.controlLabel}
											control={
												<Mui.Switch
													checked={useCustomLocationPlaceholder}
													onChange={(e) => this.setState({ useCustomLocationPlaceholder: e.target.checked })}
													color="secondary"
													className={classes.switchControl}
												/>
											}
											label={
												<Mui.Box>
													<Mui.Typography style={{ fontWeight: "bold" }}>Use custom placeholder text</Mui.Typography>
													<Mui.Typography variant="body2" className={classes.controlLabelDescription}>Customize the placeholder text that displays in the Basic &amp; Quick Search location field.</Mui.Typography>
												</Mui.Box>
											}
										/>
										{useCustomLocationPlaceholder && (
											<Mui.Box style={{ maxWidth: 480 }} className={classes.toggleSubContent}>
												{env.refreshDesignsDev && (
													<Mui.Typography component="label" className={classes.labelAbove}>
														Placeholder text
													</Mui.Typography>
												)}
												<Mui.TextField
													fullWidth
													// Label-above in the refresh (see labelAbove); MUI's floating label
													// off-flag. size="small" would override the themed 12px/16px padding.
													label={env.refreshDesignsDev ? undefined : "Placeholder Text"}
													value={locationPlaceholder}
													placeholder="Search locations"
													error={submittedLocationPlaceholderError && locationPlaceholder.trim() === ""}
													helperText={submittedLocationPlaceholderError && locationPlaceholder.trim() === "" ? "Placeholder text is required." : undefined}
													onChange={(e) => this.setState({ locationPlaceholder: e.target.value })}
													variant="outlined"
													size={env.refreshDesignsDev ? "medium" : "small"}
												/>
											</Mui.Box>
										)}
									</Mui.Box>
								)}

								{bothSubSections && <Mui.Divider style={{ marginBottom: 24 }} />}

								{showSmart && (
									<Mui.Box>
										{bothSubSections ? (
											<Mui.Typography variant="subtitle2" className={classes.sectionSubheading} style={{ marginBottom: 8 }}>Smart Search</Mui.Typography>
										) : (
											<Mui.Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>Customize the placeholder text shown in the Smart Search input field to guide website visitors.</Mui.Typography>
										)}
										<Mui.FormControlLabel
											className={classes.controlLabel}
											control={
												<Mui.Switch
													checked={useCustomPlaceholder}
													onChange={(e) => this.setState({ useCustomPlaceholder: e.target.checked })}
													color="secondary"
													className={classes.switchControl}
												/>
											}
											label={
												<Mui.Box>
													<Mui.Typography style={{ fontWeight: "bold" }}>Use custom placeholder text</Mui.Typography>
													<Mui.Typography variant="body2" className={classes.controlLabelDescription}>Rotate through custom phrases shown in the Smart Search input field.</Mui.Typography>
												</Mui.Box>
											}
										/>
										{useCustomPlaceholder && (
											<Mui.Box className={classes.toggleSubContent}>
												<Mui.Button size="small" color="primary" className={classes.editButton} onClick={() => this.setState({ showPlaceholdersModal: true })}>
													Edit <Icons.Edit size={13} style={{ marginLeft: 4 }} />
												</Mui.Button>
												<Mui.Typography variant="body2" className={classes.readOnlyList}>
													{smartSuggestions.filter(s => s.trim() !== "").join(", ") || "None added"}
												</Mui.Typography>
											</Mui.Box>
										)}
									</Mui.Box>
								)}
							</FullPageCard>
						</Mui.Box>
					);
				})()}
			</>
		);
	}

	// The "Search Results" tab — how results are presented.
	private renderSearchResultsTab() {
		const { user, classes = {} } = this.props;
		const {
			defaultResultsView,
			showSoldListings,
			prioritizeOwnListings,
			limitSoldResultsByMonths,
			showCustomPriceRangeOptions,
			minPriceOptions,
			maxPriceOptions,
			eurekaResultsSort,
			activeResultsSort,
			listingDetailModal,
		} = this.state;
		const eurekaSearch = user.permissions.eurekaSearch && user.eurekaEnabled;

		return (
			<>
				<Mui.Box mb={2}>
					<FullPageCard>
						<Mui.Box display="flex" alignItems="center" mb={2}>
							<Mui.Typography variant="h6" className={classes.sectionHeading} style={{ marginBottom: 0 }}>Search Results</Mui.Typography>
							<Mui.Tooltip title="Configure how search results are displayed and which listing types are included.">
								<Mui.IconButton size="small" style={{ marginLeft: 8, padding: 0 }}><Icons.Info size={16} /></Mui.IconButton>
							</Mui.Tooltip>
						</Mui.Box>

						<Mui.Box mb={3}>
							<Mui.Typography variant="subtitle2" className={classes.sectionSubheading}>Default Search Results View (Desktop)</Mui.Typography>
							<Mui.Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>Sets the default view for listing search results on desktop. Mobile visitors will be shown the list view by default.</Mui.Typography>
							<Mui.RadioGroup
								value={defaultResultsView}
								onChange={(e) => this.handleDefaultResultsViewChange(e.target.value as "map" | "list")}
							>
								<Mui.FormControlLabel value="map" control={<Mui.Radio color="secondary" />} label="Show List & Map" />
								<Mui.FormControlLabel value="list" control={<Mui.Radio color="secondary" />} label="Show List Only" />
							</Mui.RadioGroup>
						</Mui.Box>

						<Mui.Divider style={{ marginBottom: 24 }} />

						<Mui.Box mb={3}>
							<Mui.Typography variant="subtitle2" className={classes.sectionSubheading}>Default Sort</Mui.Typography>
							<Mui.Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>Default sort order for search results and markets.</Mui.Typography>
							{eurekaSearch ? (
								<FullWidthDropdown
									label="Sort listings by..."
									onChange={(value: string) => this.setState({ eurekaResultsSort: EurekaResultsSortType.getByIdOrDefault(value) })}
									value={eurekaResultsSort.id}
									maxWidth={400}
								>
									{EurekaResultsSortType.values().map((value) => (
										<Mui.MenuItem key={value.id} value={value.id}>{value.label}</Mui.MenuItem>
									))}
								</FullWidthDropdown>
							) : (
								<FullWidthDropdown
									label="Sort listings by..."
									onChange={(value: string) => this.setState({ activeResultsSort: ActiveResultsSortType.getByIdOrDefault(value) })}
									value={activeResultsSort.id}
									maxWidth={400}
								>
									{ActiveResultsSortType.values().map((value) => (
										<Mui.MenuItem key={value.id} value={value.id}>{value.label}</Mui.MenuItem>
									))}
								</FullWidthDropdown>
							)}
						</Mui.Box>

						<Mui.Divider style={{ marginBottom: 24 }} />

						<Mui.Box>
							<Mui.Typography variant="subtitle2" className={classes.sectionSubheading} style={{ marginBottom: 16 }}>Listings</Mui.Typography>
							<Mui.FormControlLabel
								className={classes.controlLabel}
								control={
									<Mui.Switch
										checked={showSoldListings}
										onChange={(e) => this.handleShowSoldListingsChange(e.target.checked)}
										color="secondary"
										className={classes.switchControl}
									/>
								}
								label={
									<Mui.Box>
										<Mui.Typography style={{ fontWeight: "bold" }}>Show Sold Listings in Search Results</Mui.Typography>
										<Mui.Typography variant="body2" className={classes.controlLabelDescription}>When enabled, website visitors can choose to include sold listings in their search results using the listing status filter.</Mui.Typography>
									</Mui.Box>
								}
							/>
							{showSoldListings && (
								<Mui.Box className={classes.toggleSubContent}>
									<FullWidthDropdown<number>
										label="Show sold in last..."
										onChange={(value) => this.setState({ limitSoldResultsByMonths: LimitSoldResultsByMonthsType.getByIdOrDefault(value) })}
										value={limitSoldResultsByMonths.id}
										maxWidth={400}
									>
										{LimitSoldResultsByMonthsType.values().map((value) => (
											<Mui.MenuItem key={value.id} value={value.id}>{value.label}</Mui.MenuItem>
										))}
									</FullWidthDropdown>
								</Mui.Box>
							)}
							<Mui.Box mt={3}>
								<Mui.FormControlLabel
									className={classes.controlLabel}
									control={
										<Mui.Switch
											checked={prioritizeOwnListings}
											onChange={(e) => this.handlePrioritizeOwnListingsChange(e.target.checked)}
											color="secondary"
											className={classes.switchControl}
										/>
									}
									label={
										<Mui.Box>
											<Mui.Typography style={{ fontWeight: "bold" }}>Prioritize Featured Listings First in Search Results</Mui.Typography>
											<Mui.Typography variant="body2" className={classes.controlLabelDescription}>Your featured listings — including co-listings — will appear at the top of search results.</Mui.Typography>
										</Mui.Box>
									}
								/>
							</Mui.Box>
						</Mui.Box>
					</FullPageCard>
				</Mui.Box>

				{env.customPriceRangeDev && (
				<Mui.Box mb={2}>
					<FullPageCard>
						<Mui.Box display="flex" alignItems="center" mb={2}>
							<Mui.Typography variant="h6" className={classes.sectionHeading} style={{ marginBottom: 0 }}>Price Range Options</Mui.Typography>
						</Mui.Box>
						<Mui.FormControlLabel
							className={classes.controlLabel}
							control={
								<Mui.Switch
									checked={showCustomPriceRangeOptions}
									onChange={(e) => this.setState({ showCustomPriceRangeOptions: e.target.checked })}
									color="secondary"
									className={classes.switchControl}
								/>
							}
							label={
								<Mui.Box>
									<Mui.Typography style={{ fontWeight: "bold" }}>Use Custom Price Range Options</Mui.Typography>
									<Mui.Typography variant="body2" className={classes.controlLabelDescription}>Enable to customize the minimum and maximum price dropdown options with values tailored to your market. Default values will be used if disabled. Website visitors can still enter any price manually.</Mui.Typography>
								</Mui.Box>
							}
						/>
						{showCustomPriceRangeOptions && (
							<Mui.Box className={classes.toggleSubContent}>
								<Mui.Button size="small" color="primary" className={classes.editButton} onClick={() => this.setState({ priceRangeModalOpen: true })}>
									Edit <Icons.Edit size={13} style={{ marginLeft: 4 }} />
								</Mui.Button>
								<Mui.Typography variant="body2" className={classes.readOnlyList}>
									<strong>Min Price:</strong> No Min{minPriceOptions.length > 0 ? ", " + minPriceOptions.map(formatPrice).join(", ") : ""}
								</Mui.Typography>
								<Mui.Typography variant="body2" className={classes.readOnlyList}>
									<strong>Max Price:</strong> {maxPriceOptions.length > 0 ? maxPriceOptions.map(formatPrice).join(", ") + ", " : ""}No Max
								</Mui.Typography>
							</Mui.Box>
						)}
					</FullPageCard>
				</Mui.Box>
				)}

				{env.refreshDesignsDev && (
				<Mui.Box mb={2}>
					<FullPageCard>
						<Mui.Box display="flex" alignItems="center" mb={2}>
							<Mui.Typography variant="h6" className={classes.sectionHeading} style={{ marginBottom: 0 }}>Listing Detail View</Mui.Typography>
						</Mui.Box>
						<Mui.Typography variant="body2" className={classes.introText} style={{ marginBottom: 16 }}>
							Choose how a listing's detail page opens when a website visitor selects a listing from the search results.
						</Mui.Typography>
						<Mui.FormControl>
							<Mui.RadioGroup
								value={listingDetailModal ? "modal" : "newTab"}
								onChange={(e) => this.setState({ listingDetailModal: e.target.value === "modal" })}
							>
								<Mui.FormControlLabel value="newTab" control={<Mui.Radio color="secondary" />} label="New Tab (default)" />
								<Mui.FormControlLabel value="modal" control={<Mui.Radio color="secondary" />} label="Pop-up Window" />
							</Mui.RadioGroup>
						</Mui.FormControl>
					</FullPageCard>
				</Mui.Box>
				)}
			</>
		);
	}

	public render() {
		const { user, classes = {}, pages, authError } = this.props;
		const dirty = this.isDirty();
		const {
			tab,
			showMarketsModal,
			showSuggestionsModal,
			showPlaceholdersModal,
			markets,
			searchSuggestions,
			smartSuggestions,
			minPriceOptions,
			maxPriceOptions,
			priceRangeModalOpen,
			showConfirmationModal,
			showSwitchToStandardModal,
			snackbarOpen,
		} = this.state;

		const title = "General";
		const listingsPage =
			pages?.find(p => p.key === 'LISTING_SEARCH_FORM')
			?? pages?.find(p => p.type.id === PageType.LISTINGS.id);
		const listingsPageUrl = listingsPage ? user.baseUrl + listingsPage.path : undefined;

		return (
			<DashboardLayout
				permitted={
					user &&
					user.admin &&
					user.kestrelVersion.all &&
					(user.permissions.listingSearch || user.permissions.listingMapSearch)
				}
				title={title}
				header={
					<Mui.Grid container direction="row" alignItems="center" justifyContent="space-between">
						<Mui.Typography variant="h1">{title}</Mui.Typography>
						{listingsPageUrl && (
							<PageHeaderLink href={listingsPageUrl}>View Property Search</PageHeaderLink>
						)}
					</Mui.Grid>
				}
			>
				<Mui.Box mb={4}>
					<Mui.Typography variant="body2" className={classes.introText}>
						Control how property search works on your website — including which search experience your website visitors use, how search results are displayed, and whether sold or featured listings appear in search results.
					</Mui.Typography>
					<Mui.Typography variant="body2" className={classes.introText}>
						Looking for Featured Listing Display Settings? Go to{" "}
						<Router.Link to={paths.settingsListingResults} className={classes.marketLink}>Listings &gt; Featured</Router.Link>
					</Mui.Typography>
				</Mui.Box>

				{this.searchAgentPermitted ? (
					<>
						<SearchSettingsTabs value={tab} onChange={(value) => this.setState({ tab: value })} />
						{tab === "experience" ? this.renderSearchExperienceTab() : this.renderSearchResultsTab()}
					</>
				) : (
					<>
						{this.renderSearchExperienceTab()}
						{this.renderSearchResultsTab()}
					</>
				)}

				<SettingsStickyFooter dirty={dirty} saving={this.state.saving} authError={authError} onSave={this.handleSave} onCancel={this.handleCancel} />


				<MarketsModal
					open={showMarketsModal}
					markets={markets}
					allMarkets={this.props.allMarkets}
					maxMarkets={Component.MAX_MARKETS}
					onSave={(updated) => this.setState({ markets: updated, showMarketsModal: false })}
					onCancel={() => this.setState({ showMarketsModal: false })}
				/>
				<TextListModal
					open={showSuggestionsModal}
					title="Edit Search Suggestions"
					subtitle="Add up to 5 search suggestions to display in your search dropdown."
					items={searchSuggestions}
					maxItems={Component.MAX_NLS_TERMS}
					fieldLabel="Search Suggestion"
					placeholder="e.g., 3-bedroom homes under $900K"
					onSave={(updated) => this.setState({ searchSuggestions: updated, showSuggestionsModal: false })}
					onCancel={() => this.setState({ showSuggestionsModal: false })}
				/>
				<TextListModal
					open={showPlaceholdersModal}
					title="Edit Search Placeholder Text"
					subtitle="Add up to 5 placeholder text options that rotate in the search bar."
					items={smartSuggestions}
					maxItems={Component.MAX_NLS_PLACEHOLDERS}
					fieldLabel="Search Placeholder Message"
					onSave={(updated) => this.setState({ smartSuggestions: updated, showPlaceholdersModal: false })}
					onCancel={() => this.setState({ showPlaceholdersModal: false })}
				/>
				<ConfirmationModal
					open={showConfirmationModal}
					onCancel={this.handleModalCancel}
					onConfirm={this.handleModalConfirm}
					user={this.props.user}
				/>
				<SwitchToStandardDialog
					open={showSwitchToStandardModal}
					onSwitch={this.handleSwitchToStandardConfirm}
					onCancel={this.handleSwitchToStandardCancel}
				/>

				<PriceRangeOptionsModal
					open={priceRangeModalOpen}
					minPriceOptions={minPriceOptions}
					maxPriceOptions={maxPriceOptions}
					onSave={(minPrices, maxPrices) => this.setState({
						minPriceOptions: minPrices,
						maxPriceOptions: maxPrices,
						priceRangeModalOpen: false,
					})}
					onCancel={() => this.setState({ priceRangeModalOpen: false })}
				/>


				<Mui.Snackbar
					open={snackbarOpen}
					autoHideDuration={3000}
					onClose={() => this.setState({ snackbarOpen: false })}
					anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				>
					<Alert onClose={() => this.setState({ snackbarOpen: false })} severity="success">
						Settings saved successfully!
					</Alert>
				</Mui.Snackbar>
				<UnsavedChangesPrompt isDirty={() => this.isDirty()} history={this.props.history} />
			</DashboardLayout>
		);
	}
}

export const SettingsSearchGeneralPage = Router.withRouter(withStyles(connect(mapStateToProps, mapDispatchToProps)(Component), styles));
